"use client";

import { motion, useInView, type Variants } from "framer-motion";
import { Facebook, Instagram, Youtube } from "lucide-react";
import Link from "next/link";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/lib/seo/config";

const INSTAGRAM_POSTS = [
	{ shortcode: "DTWCZrSkqov" },
	{ shortcode: "DTGxYThknDu" },
	{ shortcode: "DL_HDL1Spdn" },
	{ shortcode: "DRcs-dZgV1J" },
	{ shortcode: "DRBOhOcDMjo" },
	{ shortcode: "DQaLyFmjt7D" },
	{ shortcode: "DRuuf7Tj4Ww" },
	{ shortcode: "DMJEbbAThwR" },
] as const;

const SECTION_CONTENT = {
	handle: "@iceagemeals",
	titlePart1: "JOIN THE",
	titleAccent: "MOVEMENT",
	description:
		"Join 30k+ foodies, athletes, and adventurers fueling their big life. Daily recipes, workout tips, and behind-the-scenes chaos.",
	socialLinks: {
		instagram: siteConfig.business.social.instagram,
		facebook: siteConfig.business.social.facebook,
		youtube: siteConfig.business.social.youtube,
	},
} as const;

const fadeIn: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, ease: "easeOut" },
	},
};

const staggerContainer: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
			delayChildren: 0.2,
		},
	},
};

export function SocialProof() {
	const sectionRef = useRef<HTMLElement>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

	return (
		<section
			ref={sectionRef}
			className="py-24 bg-white dark:bg-black text-black dark:text-white overflow-hidden relative border-t border-b border-gray-100 dark:border-gray-900"
		>
			<div className="container mx-auto px-4 relative z-10">
				<motion.div
					initial="hidden"
					animate={isInView ? "visible" : "hidden"}
					variants={fadeIn}
					className="flex flex-col md:flex-row items-end justify-between mb-16 gap-6"
				>
					<div className="space-y-4 max-w-2xl">
						<div className="inline-flex items-center gap-2 text-primary font-bold tracking-wider uppercase text-sm">
							<Instagram className="w-4 h-4" /> {SECTION_CONTENT.handle}
						</div>
						<h2 className="text-4xl md:text-6xl font-black tracking-tighter">
							{SECTION_CONTENT.titlePart1}{" "}
							<span className="text-transparent bg-clip-text bg-linear-to-r from-purple-400 to-pink-600">
								{SECTION_CONTENT.titleAccent}
							</span>
						</h2>
						<p className="text-xl text-zinc-600 dark:text-zinc-400">
							{SECTION_CONTENT.description}
						</p>
					</div>

					<div className="flex gap-4">
						<Link
							href={SECTION_CONTENT.socialLinks.instagram}
							target="_blank"
							rel="noopener noreferrer"
						>
							<Button
								variant="outline"
								size="icon"
								className="rounded-full h-12 w-12 border-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
								aria-label="Follow us on Instagram"
							>
								<Instagram className="w-5 h-5" />
							</Button>
						</Link>
						<Link
							href={SECTION_CONTENT.socialLinks.facebook}
							target="_blank"
							rel="noopener noreferrer"
						>
							<Button
								variant="outline"
								size="icon"
								className="rounded-full h-12 w-12 border-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
								aria-label="Follow us on Facebook"
							>
								<Facebook className="w-5 h-5" />
							</Button>
						</Link>
						<Link
							href={SECTION_CONTENT.socialLinks.youtube}
							target="_blank"
							rel="noopener noreferrer"
						>
							<Button
								variant="outline"
								size="icon"
								className="rounded-full h-12 w-12 border-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
								aria-label="Subscribe to our YouTube channel"
							>
								<Youtube className="w-5 h-5" />
							</Button>
						</Link>
					</div>
				</motion.div>

				<motion.div
					variants={staggerContainer}
					initial="hidden"
					animate={isInView ? "visible" : "hidden"}
					className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
				>
					{INSTAGRAM_POSTS.map((post, i) => (
						<motion.div
							// biome-ignore lint/suspicious/noArrayIndexKey: Static posts
							key={i}
							variants={fadeIn}
							whileHover={{ scale: 1.03 }}
							transition={{ type: "spring", stiffness: 300, damping: 22 }}
							className="group relative aspect-4/5 rounded-xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 shadow-sm hover:shadow-2xl hover:border-zinc-300 dark:hover:border-zinc-600 overflow-hidden cursor-pointer transition-all duration-200"
						>
							<div className="absolute inset-0 overflow-hidden">
								<iframe
									src={`https://www.instagram.com/p/${post.shortcode}/embed/`}
									title={`Instagram post ${i + 1}`}
									loading="lazy"
									allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
									className="absolute inset-0 w-full h-full border-0 pointer-events-none"
									scrolling="no"
								/>
							</div>

							<a
								href={`https://www.instagram.com/p/${post.shortcode}/`}
								target="_blank"
								rel="noopener noreferrer"
								className="absolute inset-0 z-10"
								aria-label={`View Instagram post ${i + 1}`}
							>
								<span className="sr-only">View Instagram post {i + 1}</span>
							</a>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}
